import { jobLists } from "character";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import RelicCheckbox from "components/relic-checkbox/RelicCheckbox";
import { quests } from "data-files/quests";
import { EorzeaDataType, GameData, Job, LinkType, RelicData, View } from "enums";
import log from "logger";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import utils from "util/utils";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponPhantomOccultumProps } from "views/relics/weapons/phantom/occultum/types";
import { TaskInfo } from "views/type";
import "./WeaponPhantomOccultum.css";

const mapStateToProps = (state:AppState):ViewWeaponPhantomOccultumProps.Redux => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
		options: state.userInfo.options,
	};
};

class WeaponPhantomOccultum extends Component<ViewWeaponPhantomOccultumProps.Component> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_PHANTOM],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_PHANTOM,
		part: RelicData.Part.WEAPON_OCCULTUM,
		steps: [RelicData.Step.KNOWLEDGE, RelicData.Step.EXCHANGE],
		stepPlaceholders: {},
		next: null,
		previous: {
			part: RelicData.Part.WEAPON_ECLIPTICUM,
			view: View.RELIC_WEAPONS_PHANTOM_ECLIPTICUM,
		},
	};

	constructor(props:ViewWeaponPhantomOccultumProps.Component){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{

		if(this.relicInfo.previous === null){
			log.error("Expected a previous part, but got none");
			return <div>Error Rendering Page</div>;
		}

		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		const knowledgeTaskInfo:TaskInfo[] = [
			{ task: RelicData.Task.TASK_1, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_1) },
			{ task: RelicData.Task.TASK_2, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_2) },
			{ task: RelicData.Task.TASK_3, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_3) },
			{ task: RelicData.Task.TASK_4, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_4) },
			{ task: RelicData.Task.TASK_5, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_5) },
			{ task: RelicData.Task.TASK_6, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_6) },
			{ task: RelicData.Task.TASK_7, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_7) },
			{ task: RelicData.Task.TASK_8, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_8) },
			{ task: RelicData.Task.TASK_9, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_9) },
			{ task: RelicData.Task.TASK_10, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_10) },
			{ task: RelicData.Task.TASK_11, target: manager.relics.getTaskOther(RelicData.Step.KNOWLEDGE, RelicData.Task.TASK_11) },
		];

		return (
			<div id="weapon-phantom-penumbrae" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection
						title={RelicData.Step.KNOWLEDGE}
						placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.KNOWLEDGE]}
						id={RelicData.Step.KNOWLEDGE}
						stepComplete={completeSteps[RelicData.Step.KNOWLEDGE]}
						key={RelicData.Step.KNOWLEDGE}
					>
						<p>{t.t("Complete")} <Link
							type={LinkType.LODESTONE}
							lodestoneType={EorzeaDataType.BLUEQUEST}
							text={GameData.Quest.FINAL_PHANTASM}
							lodestoneID={quests[GameData.Quest.FINAL_PHANTASM].lodestoneID}
						/></p>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Target")}</th><th>{t.t("Done")}</th></tr></thead>
								<tbody>
									{
										knowledgeTaskInfo.map((thisTask) => {
											const thisComplete = character.progress.isComplete(
												this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.KNOWLEDGE, thisTask.task, Job.PLD,
											);
											if((this.props.hideCompletedSteps || this.props.hideCompletedTasks) && thisComplete){ return null; }

											return <tr key={`${RelicData.Step.KNOWLEDGE}-${thisTask.task}`}>
												<td>{t.t(thisTask.target)}</td>
												<td><RelicCheckbox character={character} job={Job.PLD} relicInfo={this.relicInfo} step={RelicData.Step.KNOWLEDGE} task={thisTask.task} /></td>
											</tr>;
										})
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.KNOWLEDGE, Job.PLD)
											? <tr className="all-complete"><td colSpan={7}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
					<DetailsSection
						title={RelicData.Step.EXCHANGE}
						placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.EXCHANGE]}
						id={RelicData.Step.EXCHANGE}
						stepComplete={completeSteps[RelicData.Step.EXCHANGE]}
						key={RelicData.Step.EXCHANGE}
					>
						<p>{t.t("Exchange your %PREVPART% relic for the %PART% version", { PREVPART: this.relicInfo.previous.part, PART: this.relicInfo.part })}</p>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount():void{
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }

		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [];
	}

	private selectJob(job:Job):void{ // Event
		const options = this.props.options;
		options.relics = {
			...options.relics,
			animaLuxStepJob: utils.guards.isEnumValue(Job, job) ? job : Job.PLD,
		};
		manager.data.saveOptions(options, true);
	}
}
export default connect(mapStateToProps)(WeaponPhantomOccultum);
